/**
 * Sidebar component — collapsible navigation with localStorage persistence
 * (W1 S0b extraction from App.tsx, moved verbatim).
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, X, PanelLeftClose, PanelLeft, Users, ExternalLink, HelpCircle, UserCircle } from 'lucide-react';
import { VersionBadge } from '@secureembed/shared-crm';
import { navGroups, w1TopNav } from './nav';
import { isW1Shell } from '../../utils/w1Flag';
import OpenExternalPortal from './OpenExternalPortal';

const Sidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onOpenHelp?: () => void;
}> = ({ isOpen, onClose, isCollapsed, onToggleCollapse, onOpenHelp }) => {
  const location = useLocation();
  const flagOn = isW1Shell();

  // Check if a path is active
  const isPathActive = (href: string) => {
    if (href.includes('?')) {
      const [basePath, queryString] = href.split('?');
      // Must match both the base path AND the query string
      return location.pathname === basePath && location.search.includes(queryString);
    }
    return location.pathname === href;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen min-h-screen bg-iflow-700 text-white transform transition-all duration-200
        lg:translate-x-0 lg:static lg:z-0 lg:h-auto lg:min-h-full flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <div className="flex items-start space-x-2">
            <Shield className="w-8 h-8 flex-shrink-0" />
            {!isCollapsed && (
              <div className="flex flex-col leading-tight mt-0.5">
                <span className="font-bold text-lg leading-tight">SecureEmbed</span>
                <span className="text-[10px] text-white/50 uppercase tracking-wider">Platform Management</span>
                <VersionBadge portal="dashboard" collapsed={false} />
              </div>
            )}
          </div>
          <button onClick={onClose} className="lg:hidden">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Collapse toggle button */}
        <div className="px-3 py-2">
          <button
            onClick={onToggleCollapse}
            className={`w-full flex items-center px-3 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <PanelLeftClose className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation. Flag ON (Bug #5625, mockup G01): a flat 8-item domain
            list, no section headers. Flag OFF: the legacy grouped sections
            (W1a), byte-for-byte unchanged. Active state matches the exact
            path (and nested sub-paths) in both cases. */}
        {flagOn ? (
          <nav className="px-2 flex-1 overflow-y-auto">
            {w1TopNav.map((item) => {
              const isActive = isPathActive(item.href)
                || (item.href !== '/' && location.pathname.startsWith(item.href + '/'));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  title={isCollapsed ? item.name : undefined}
                  aria-label={isCollapsed ? item.name : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  className={`
                    flex items-center px-3 py-2 rounded-lg mb-0.5 transition-colors
                    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white
                    ${isCollapsed ? 'justify-center' : ''}
                    ${isActive
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span className="truncate ml-3 text-sm">{item.name}</span>}
                </Link>
              );
            })}
          </nav>
        ) : (
        <nav className="px-2 flex-1 overflow-y-auto">
          {navGroups.map((group, gi) => (
            <div key={group.section ?? `group-${gi}`} className="mb-1">
              {group.section && !isCollapsed && (
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                  {group.section}
                </div>
              )}
              {group.section && isCollapsed && gi > 0 && (
                <div className="mx-3 my-2 border-t border-white/10" />
              )}
              {group.items.map((item) => {
                const isActive = isPathActive(item.href)
                  || (item.href !== '/' && location.pathname.startsWith(item.href + '/'));
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={onClose}
                    title={isCollapsed ? item.name : undefined}
                    aria-label={isCollapsed ? item.name : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    className={`
                      flex items-center px-3 py-2 rounded-lg mb-0.5 transition-colors
                      focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white
                      ${isCollapsed ? 'justify-center' : ''}
                      ${isActive
                        ? 'bg-white/20 text-white font-medium'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'}
                    `}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && <span className="truncate ml-3 text-sm">{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        )}

        {/* External portal link. Flag OFF: original single hard-coded
            CloudFront link, byte-for-byte unchanged. Flag ON: G09's
            allow-listed, confirmed navigation (OpenExternalPortal) —
            replaces this exact hard-coded-URL gap the contract names. */}
        {!flagOn && !isCollapsed && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <a
                href="https://d2k5ne4hoaiwmb.cloudfront.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors text-sm"
              >
                <Users className="w-4 h-4 flex-shrink-0" />
                <span className="truncate ml-3">Stakeholder Portal</span>
                <ExternalLink className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
              </a>
            </div>
          </div>
        )}
        {!flagOn && isCollapsed && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <a
                href="https://d2k5ne4hoaiwmb.cloudfront.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title="Stakeholder Portal"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
        )}
        {flagOn && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <OpenExternalPortal collapsed={isCollapsed} />
            </div>
          </div>
        )}

        {/* G01 sidebar footer: Help + My account (Bug #5625). Help opens the
            same HelpDrawer as Header's G07 trigger — onOpenHelp is threaded
            from Layout, which owns the drawer's open state. My account links
            to the existing /my-account/profile route. */}
        {flagOn && (
          <div className="flex-shrink-0 px-2 pb-3">
            <div className="border-t border-white/10 pt-2">
              <button
                type="button"
                onClick={onOpenHelp}
                title={isCollapsed ? 'Help' : undefined}
                aria-label={isCollapsed ? 'Help' : undefined}
                className={`w-full flex items-center px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span className="truncate ml-3 text-sm">Help</span>}
              </button>
              <Link
                to="/my-account/profile"
                onClick={onClose}
                title={isCollapsed ? 'My account' : undefined}
                aria-label={isCollapsed ? 'My account' : undefined}
                aria-current={isPathActive('/my-account/profile') ? 'page' : undefined}
                className={`flex items-center px-3 py-2 rounded-lg transition-colors ${isCollapsed ? 'justify-center' : ''} ${
                  isPathActive('/my-account/profile')
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <UserCircle className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span className="truncate ml-3 text-sm">My account</span>}
              </Link>
            </div>
          </div>
        )}

        <div className="flex-shrink-0 h-4" />
      </aside>
    </>
  );
};

export default Sidebar;
